/**
 * Job-details payload shaping (#340 item 4).
 *
 * Two pure functions JobDetailsSection composes:
 *
 * - buildJobDetailsPatch — form state → the `job_details` workspace patch.
 *   Encodes the shaping rule that a driver only rides along when the vehicle
 *   is the contractor's personal one. `day_schedules` is always present: the
 *   server full-replaces the schedule set whenever the key exists, so an empty
 *   array is how the section says "this job has no phase rows". The section
 *   feeds the same patch to its savable-section snapshot, so "dirty" is defined
 *   as "the payload would differ" by construction.
 *
 * - buildDefaultDaySchedules — seed one `event`-kind row per event day for a
 *   booking that has none saved yet: times copied from the single-day fields
 *   and event start/end parsed from the booking's "HH:MM - HH:MM" window.
 *   When the quote priced labour (#181), the setup/pack-down/travel hours seed
 *   the schedule too: a setup row when setup labour exists, a packout row when
 *   pack-down labour exists (times blank — only the admin knows them, #87 —
 *   but the quoted hours ride along in the row's notes so the schedule and the
 *   quote's labour agree instead of living in two places). Travel has no phase,
 *   so its hours annotate the event row.
 */
import type {
  DaySchedulePayload,
  JobDetailsPatch,
} from "./event-workspace-api";
import { toTimeValue } from "./format-utils";

export interface JobDetailsFormState {
  callTime: string;
  packOutTime: string;
  siteAvailableFrom: string;
  callOutNotes: string;
  schedules: DaySchedulePayload[];
}

export function buildJobDetailsPatch(
  form: JobDetailsFormState,
): JobDetailsPatch {
  return {
    call_time: form.callTime,
    pack_out_time: form.packOutTime,
    site_available_from: form.siteAvailableFrom,
    call_out_notes: form.callOutNotes,
    // Always sent. Presence means "replace the whole set", so an empty array is
    // the only way to express "the admin deleted the last phase row". Gating on
    // isMultiDay (as this did) made a phase day impossible on a one-day event —
    // the setup row had nowhere to live unless the event window itself was
    // stretched to cover it, which corrupts the client-facing event date (#87).
    day_schedules: form.schedules,
  };
}

/**
 * Quoted labour hours per phase (#181). Server-folded by internal/labour and
 * shipped on the workspace read (quotedLabour.by_category, #242 C4) — the SPA
 * never re-sums the quote's service rows.
 */
export interface QuotedLabour {
  setup: number;
  operation: number;
  pack_down: number;
  travel: number;
}

/** "4h", "1.5h", "0.5h". */
function hoursLabel(hours: number): string {
  return `${hours}h`;
}

function blankPhaseRow(
  kind: DaySchedulePayload["kind"],
  scheduleDate: string,
  notes: string,
): DaySchedulePayload {
  return {
    day_number: 0,
    schedule_date: scheduleDate,
    kind,
    site_open: "",
    call_time: "",
    event_start: "",
    event_end: "",
    pack_out: "",
    ready_by: "",
    notes,
  };
}

export function buildDefaultDaySchedules(input: {
  eventDate: string;
  dayCount: number;
  /** The booking's "start - end" window, e.g. "18:00 - 23:00" (or null). */
  eventTime: string | null;
  siteAvailableFrom: string;
  callTime: string;
  packOutTime: string;
  /** Quoted labour hours per phase — seeds setup/packout rows + travel note. */
  quotedLabour?: QuotedLabour | null;
}): DaySchedulePayload[] {
  const start = new Date(input.eventDate);
  let evStart = "";
  let evEnd = "";
  if (input.eventTime) {
    const parts = input.eventTime.split(/\s*-\s*/);
    if (parts.length === 2) {
      evStart = toTimeValue(parts[0]);
      evEnd = toTimeValue(parts[1]);
    }
  }
  const labour = input.quotedLabour;
  // Travel is not a phase — its hours annotate the first event row.
  const travelNote =
    labour && labour.travel > 0
      ? `Travel: ${hoursLabel(labour.travel)} quoted`
      : "";

  const eventRows = Array.from({ length: input.dayCount }, (_, i) => {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    const dateStr = date.toISOString().split("T")[0];
    return {
      day_number: i + 1,
      schedule_date: dateStr,
      // Every seeded row is an event day: these are the dates inside the event
      // window. The old first/last "Setup"/"Packdown" notes labels are gone —
      // `kind` carries the phase now, and those labels were decorative anyway
      // (a first day inside the event window is not a separate setup day).
      kind: "event" as const,
      site_open: toTimeValue(input.siteAvailableFrom),
      call_time: toTimeValue(input.callTime),
      event_start: evStart,
      event_end: evEnd,
      pack_out: toTimeValue(input.packOutTime),
      ready_by: "",
      notes: i === 0 ? travelNote : "",
    };
  });

  if (!labour) return eventRows;

  const rows: DaySchedulePayload[] = [...eventRows];
  const firstDate = eventRows[0]?.schedule_date ?? input.eventDate;
  const lastDate =
    eventRows[eventRows.length - 1]?.schedule_date ?? input.eventDate;
  // Setup rides in front, packout behind, both on the event's own date — the
  // admin pulls them onto a separate day if the phase warrants it (#87). Times
  // stay blank; the quoted hours are what carry over, in the notes.
  if (labour.setup > 0) {
    rows.unshift(
      blankPhaseRow(
        "setup",
        firstDate,
        `Setup labour: ${hoursLabel(labour.setup)} quoted`,
      ),
    );
  }
  if (labour.pack_down > 0) {
    rows.push(
      blankPhaseRow(
        "packout",
        lastDate,
        `Pack-down labour: ${hoursLabel(labour.pack_down)} quoted`,
      ),
    );
  }
  // Stable sort keeps setup ahead of / packout behind the event rows they tie
  // on, and renumbers day_number to the server's reinsert key.
  return renumberByDate(rows);
}

/** Shift an ISO date by whole days, staying in ISO. */
function shiftISODate(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

/**
 * Append a blank day dated the day after the last row, mirroring the gear
 * editor's "Add gear": the row arrives empty and the admin fills it in. Phase
 * is a column in that row, so there is no need for separate add-setup and
 * add-packout actions — and no need for reorder controls either, since rows
 * sort by date and redating a row *is* the reorder.
 *
 * Kind defaults to packout: appending lands after the event, which is what a
 * pack-out day is. A setup day is the same row with its date pulled back and
 * the phase switched — two edits either way.
 *
 * Rows come back sorted by date and renumbered, because day_number is the
 * UNIQUE key the server reinserts against and the PDF orders by.
 */
export function appendDay(
  schedules: DaySchedulePayload[],
  fallbackDate: string,
): DaySchedulePayload[] {
  const dates = schedules.map((s) => s.schedule_date).sort();
  const last = dates[dates.length - 1];
  const row: DaySchedulePayload = {
    day_number: 0,
    // The first row of an empty table is the event day itself; every row after
    // it lands the day after the last one.
    schedule_date: last ? shiftISODate(last, 1) : fallbackDate,
    kind: last ? "packout" : "event",
    site_open: "",
    call_time: "",
    event_start: "",
    event_end: "",
    pack_out: "",
    ready_by: "",
    notes: "",
  };
  return renumberByDate([...schedules, row]);
}

/** Sort by date and reassign day_number 1..N to match. */
export function renumberByDate(
  schedules: DaySchedulePayload[],
): DaySchedulePayload[] {
  return [...schedules]
    .sort((a, b) => a.schedule_date.localeCompare(b.schedule_date))
    .map((s, i) => ({ ...s, day_number: i + 1 }));
}
